/**
 * A small set of mutually exclusive choices shown side by side.
 *
 * Used where a radio list would take a line per option in a long list of rows
 * — the permission editor's "role default / grant / revoke", seventy-odd times
 * on one page. Each option is a toggle button (`aria-pressed`) inside a named
 * group, so a screen reader hears the group, the option and whether it is on.
 *
 * INK IS SET HERE, NOT INHERITED. HeroUI's neutral `foreground` is white (the
 * ink ON a solid neutral fill, `hero.ts`), which is what made unselected
 * bordered buttons unreadable elsewhere. Every pairing below is measured:
 * white on success-600 5.4:1, on danger-600 5.9:1, on default-800 10.4:1;
 * default-800 on white 10.4:1.
 *
 * Direction: a flex row with logical rounding (`rounded-s` / `rounded-e`), so
 * the first option sits at the start edge in both languages.
 */

export type SegmentTone = 'neutral' | 'success' | 'danger';

export interface SegmentOption<Value extends string> {
  readonly value: Value;
  /** Already translated. */
  readonly label: string;
  readonly tone?: SegmentTone;
}

const SELECTED: Record<SegmentTone, string> = {
  neutral: 'border-default-800 bg-default-800 text-white',
  success: 'border-success-600 bg-success-600 text-white',
  danger: 'border-danger-600 bg-danger-600 text-white',
};

export function SegmentedControl<Value extends string>({
  label,
  value,
  options,
  onChange,
  isDisabled = false,
}: {
  /** Names the group for assistive technology. Already translated. */
  readonly label: string;
  readonly value: Value;
  readonly options: readonly SegmentOption<Value>[];
  readonly onChange: (value: Value) => void;
  readonly isDisabled?: boolean;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex max-w-full flex-wrap">
      {options.map((option, index) => {
        const isSelected = option.value === value;
        const edge =
          index === 0
            ? 'rounded-s-medium'
            : index === options.length - 1
              ? 'rounded-e-medium'
              : '';
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            disabled={isDisabled}
            onClick={() => {
              if (!isSelected) onChange(option.value);
            }}
            className={`-ms-px min-h-8 border px-3 py-1 text-caption font-medium outline-none transition-colors first:ms-0 focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60 ${edge} ${
              isSelected
                ? SELECTED[option.tone ?? 'neutral']
                : 'border-default-300 bg-white text-default-800 hover:bg-default-100'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
